import Mention from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extensions';
import { PluginKey } from '@tiptap/pm/state';
import { EditorContent, useEditor, type Editor, type JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionProps } from '@tiptap/suggestion';
import { Bold, Code, Italic, List, ListOrdered, Strikethrough } from 'lucide-react';
import {
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { isRichTextEmpty, richTextToPlain, type RichNode } from '@regent/shared';
import { cn } from '@/lib/cn';
import { Avatar } from './avatar';

export interface MentionItem {
  id: string;
  label: string;
  image?: string | null;
  hint?: string;
}

export interface RichTextHandle {
  clear(): void;
  focus(): void;
  insertText(text: string): void;
  /** Inserts a `#task` reference chip. */
  insertTaskMention(id: string, label: string): void;
  /** Inserts linked text. */
  insertLink(href: string, text: string): void;
  getJSON(): JSONContent;
  isEmpty(): boolean;
}

interface SuggestState {
  items: MentionItem[];
  index: number;
  rect: DOMRect | null;
  command: (item: MentionItem) => void;
}

export interface RichTextInputProps {
  /** Initial content (Tiptap JSON). The editor is uncontrolled; use the ref to clear it. */
  defaultValue?: unknown;
  onChange?: (json: JSONContent, text: string) => void;
  /** Enter submits (Shift+Enter for a new line) when set. Otherwise Mod+Enter submits. */
  onSubmit?: (json: JSONContent, text: string) => void;
  submitOnEnter?: boolean;
  onBlur?: (json: JSONContent, text: string) => void;
  placeholder?: string;
  /** People (or other things) that can be mentioned with @. */
  mentions?: MentionItem[];
  /** Enables `#` task references: return matching tasks (id, label, optional hint) for the typed text. */
  taskSearch?: (query: string) => Promise<MentionItem[]> | MentionItem[];
  editable?: boolean;
  autoFocus?: boolean;
  toolbar?: boolean;
  className?: string;
  /** Extra controls rendered at the end of the toolbar row (send button, emoji, attach). */
  actions?: ReactNode;
  ref?: Ref<RichTextHandle>;
  'aria-label'?: string;
}

/** Lightweight rich text input for comments and chat: marks, lists, links and @mentions. */
export function RichTextInput({
  defaultValue,
  onChange,
  onSubmit,
  submitOnEnter,
  onBlur,
  placeholder = 'Write something',
  mentions,
  taskSearch,
  editable = true,
  autoFocus,
  toolbar = true,
  className,
  actions,
  ref,
  'aria-label': ariaLabel,
}: RichTextInputProps) {
  const [suggest, setSuggest] = useState<SuggestState | null>(null);
  // Refs keep the Tiptap callbacks (created once) pointed at the latest props and state.
  const latest = useRef({ onSubmit, onChange, onBlur, submitOnEnter, mentions, taskSearch, suggest });
  latest.current = { onSubmit, onChange, onBlur, submitOnEnter, mentions, taskSearch, suggest };

  /** Shared popup wiring for the @ and # suggestion lists. */
  const suggestionRenderer = () => ({
    onStart: (props: SuggestionProps<MentionItem>) =>
      setSuggest({
        items: props.items,
        index: 0,
        rect: props.clientRect?.() ?? null,
        command: (item) => props.command({ id: item.id, label: item.label } as never),
      }),
    onUpdate: (props: SuggestionProps<MentionItem>) =>
      setSuggest({
        items: props.items,
        index: 0,
        rect: props.clientRect?.() ?? null,
        command: (item) => props.command({ id: item.id, label: item.label } as never),
      }),
    onExit: () => setSuggest(null),
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      const state = latest.current.suggest;
      if (!state || state.items.length === 0) return false;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setSuggest({ ...state, index: (state.index + delta + state.items.length) % state.items.length });
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        state.command(state.items[state.index]!);
        return true;
      }
      if (event.key === 'Escape') {
        setSuggest(null);
        return true;
      }
      return false;
    },
  });

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true },
      }),
      Placeholder.configure({ placeholder }),
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          char: '@',
          pluginKey: new PluginKey('mention-people'),
          items: ({ query }) => {
            const q = query.toLowerCase();
            return (latest.current.mentions ?? [])
              .filter((m) => m.label.toLowerCase().includes(q) || m.hint?.toLowerCase().includes(q))
              .slice(0, 8);
          },
          render: suggestionRenderer,
        },
      }),
      // `#` references a task. The node is stored as `taskMention` so readers can show a task card.
      Mention.extend({ name: 'taskMention' }).configure({
        HTMLAttributes: { class: 'mention task-mention' },
        renderText: ({ node }) => `#${node.attrs.label ?? node.attrs.id}`,
        renderHTML: ({ options, node }) => [
          'span',
          { ...options.HTMLAttributes, 'data-task-id': node.attrs.id },
          `#${node.attrs.label ?? node.attrs.id}`,
        ],
        suggestion: {
          char: '#',
          pluginKey: new PluginKey('mention-tasks'),
          items: async ({ query }) => {
            const search = latest.current.taskSearch;
            if (!search) return [];
            try {
              return (await search(query)).slice(0, 8);
            } catch {
              return [];
            }
          },
          render: suggestionRenderer,
        },
      }),
    ],

    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content: (defaultValue as JSONContent | null | undefined) ?? undefined,
    editable,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        class: 'rich-text min-h-[1.5rem] text-sm',
        role: 'textbox',
        'aria-multiline': 'true',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
      handleKeyDown: (_view, event) => {
        const { onSubmit: submit, submitOnEnter: onEnter, suggest: open } = latest.current;
        if (!submit || event.key !== 'Enter' || open) return false;
        const mod = event.metaKey || event.ctrlKey;
        if ((onEnter && !event.shiftKey && !mod) || (!onEnter && mod)) {
          const instance = editorRef.current;
          if (instance && !isRichTextEmpty(instance.getJSON())) {
            const json = instance.getJSON();
            submit(json, richTextToPlain(json));
          }
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      const json = e.getJSON();
      latest.current.onChange?.(json, richTextToPlain(json));
    },
    onBlur: ({ editor: e }) => {
      const json = e.getJSON();
      latest.current.onBlur?.(json, richTextToPlain(json));
    },
  });
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  useImperativeHandle(
    ref,
    () => ({
      clear: () => editor?.commands.clearContent(true),
      focus: () => editor?.commands.focus('end'),
      insertText: (text) => editor?.chain().focus().insertContent(text).run(),
      insertTaskMention: (id, label) =>
        editor
          ?.chain()
          .focus()
          .insertContent([
            { type: 'taskMention', attrs: { id, label } },
            { type: 'text', text: ' ' },
          ])
          .run(),
      insertLink: (href, text) =>
        editor
          ?.chain()
          .focus()
          .insertContent([
            { type: 'text', text, marks: [{ type: 'link', attrs: { href } }] },
            { type: 'text', text: ' ' },
          ])
          .run(),
      getJSON: () => editor?.getJSON() ?? { type: 'doc', content: [] },
      isEmpty: () => !editor || isRichTextEmpty(editor.getJSON()),
    }),
    [editor],
  );

  const tools: { label: string; icon: ReactNode; active: string; run: (e: Editor) => void }[] = [
    { label: 'Bold', icon: <Bold />, active: 'bold', run: (e) => e.chain().focus().toggleBold().run() },
    {
      label: 'Italic',
      icon: <Italic />,
      active: 'italic',
      run: (e) => e.chain().focus().toggleItalic().run(),
    },
    {
      label: 'Strikethrough',
      icon: <Strikethrough />,
      active: 'strike',
      run: (e) => e.chain().focus().toggleStrike().run(),
    },
    { label: 'Code', icon: <Code />, active: 'code', run: (e) => e.chain().focus().toggleCode().run() },
    {
      label: 'Bulleted list',
      icon: <List />,
      active: 'bulletList',
      run: (e) => e.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Numbered list',
      icon: <ListOrdered />,
      active: 'orderedList',
      run: (e) => e.chain().focus().toggleOrderedList().run(),
    },
  ];

  return (
    <div
      className={cn(
        'rounded-lg border border-border-strong bg-surface transition-colors focus-within:border-accent',
        !editable && 'border-transparent bg-transparent',
        className,
      )}
    >
      <EditorContent editor={editor} className="max-h-72 overflow-y-auto px-3 py-2" />
      {editable && (toolbar || actions) ? (
        <div className="flex items-center gap-0.5 px-1.5 pb-1.5">
          {toolbar && editor
            ? tools.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  title={t.label}
                  aria-label={t.label}
                  aria-pressed={editor.isActive(t.active)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => t.run(editor)}
                  className={cn(
                    'flex size-6 items-center justify-center rounded text-fg-subtle hover:bg-hover hover:text-fg [&_svg]:size-3.5',
                    editor.isActive(t.active) && 'bg-hover text-fg',
                  )}
                >
                  {t.icon}
                </button>
              ))
            : null}
          <span className="ml-auto flex items-center gap-1">{actions}</span>
        </div>
      ) : null}
      {suggest && suggest.items.length > 0 && suggest.rect
        ? createPortal(
            <div
              role="listbox"
              aria-label="Mention"
              className="fixed z-[80] w-60 animate-pop rounded-lg border border-border bg-surface p-1 shadow-pop"
              style={{
                left: Math.min(suggest.rect.left, window.innerWidth - 256),
                ...(suggest.rect.bottom + 260 > window.innerHeight
                  ? { bottom: window.innerHeight - suggest.rect.top + 6 }
                  : { top: suggest.rect.bottom + 6 }),
              }}
            >
              {suggest.items.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={i === suggest.index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    suggest.command(item);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                    i === suggest.index && 'bg-hover',
                  )}
                >
                  <Avatar name={item.label} id={item.id} image={item.image} size="xs" />
                  <span className="truncate">{item.label}</span>
                  {item.hint ? (
                    <span className="ml-auto truncate text-xs text-fg-subtle">{item.hint}</span>
                  ) : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// ---------- Read-only rendering ----------

/** Up to three capitalised words after an @, so "@Alex Wolf" in plain text reads like a real mention. */
const NAME_MENTION = /@(\p{Lu}[\p{L}'’.-]*(?: \p{Lu}[\p{L}'’.-]*){0,2})/gu;

/** Splits text so that "@Name" runs render as mention chips (known people get their id). */
function textWithMentions(text: string, people: MentionItem[] | undefined): ReactNode {
  if (!text.includes('@')) return text;
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(NAME_MENTION)) {
    const index = match.index ?? 0;
    const name = match[1]!;
    const person = people?.find((p) => p.label.toLowerCase() === name.toLowerCase());
    if (index > last) parts.push(text.slice(last, index));
    parts.push(
      <span key={index} className="mention" data-mention-id={person?.id ?? ''}>
        @{person?.label ?? name}
      </span>,
    );
    last = index + match[0].length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function renderMarks(node: RichNode, key: number, people?: MentionItem[]): ReactNode {
  const code = node.marks?.some((m) => m.type === 'code');
  let out: ReactNode = code ? (node.text ?? '') : textWithMentions(node.text ?? '', people);
  for (const mark of node.marks ?? []) {
    if (mark.type === 'bold') out = <strong>{out}</strong>;
    else if (mark.type === 'italic') out = <em>{out}</em>;
    else if (mark.type === 'strike') out = <s>{out}</s>;
    else if (mark.type === 'underline') out = <u>{out}</u>;
    else if (mark.type === 'code') out = <code>{out}</code>;
    else if (mark.type === 'link') {
      const href = String(mark.attrs?.href ?? '');
      // Only render safe protocols as links.
      out = /^(https?:|mailto:|\/)/i.test(href) ? (
        <a href={href} target="_blank" rel="noopener noreferrer nofollow">
          {out}
        </a>
      ) : (
        out
      );
    }
  }
  return <Fragment key={key}>{out}</Fragment>;
}

function renderNode(node: RichNode, key: number, people?: MentionItem[]): ReactNode {
  const children = node.content?.map((child, i) => renderNode(child, i, people));
  switch (node.type) {
    case 'doc':
      return <Fragment key={key}>{children}</Fragment>;
    case 'text':
      return renderMarks(node, key, people);
    case 'paragraph':
      return <p key={key}>{children?.length ? children : <br />}</p>;
    case 'heading': {
      const level = Math.min(3, Math.max(1, Number(node.attrs?.level ?? 1)));
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3';
      return <Tag key={key}>{children}</Tag>;
    }
    case 'bulletList':
      return <ul key={key}>{children}</ul>;
    case 'orderedList':
      return <ol key={key}>{children}</ol>;
    case 'listItem':
      return <li key={key}>{children}</li>;
    case 'blockquote':
      return <blockquote key={key}>{children}</blockquote>;
    case 'codeBlock':
      return (
        <pre key={key}>
          <code>{children}</code>
        </pre>
      );
    case 'hardBreak':
      return <br key={key} />;
    case 'horizontalRule':
      return <hr key={key} />;
    case 'mention':
      return (
        <span key={key} className="mention" data-mention-id={String(node.attrs?.id ?? '')}>
          @{String(node.attrs?.label ?? node.attrs?.id ?? '')}
        </span>
      );
    case 'taskMention':
      return (
        <span
          key={key}
          role="link"
          tabIndex={0}
          className="mention task-mention cursor-pointer hover:underline"
          data-task-id={String(node.attrs?.id ?? '')}
        >
          #{String(node.attrs?.label ?? node.attrs?.id ?? '')}
        </span>
      );
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
}

/** Renders Tiptap JSON as static React elements. Cheap enough for long comment and chat lists. */
export function RichTextView({
  value,
  className,
  onTaskClick,
  people,
}: {
  value: unknown;
  className?: string;
  /** Called with the task id when a `#task` reference is clicked or activated with Enter. */
  onTaskClick?: (taskId: string) => void;
  /** People to recognise in plain "@Name" text, such as mentions pasted or imported as text. */
  people?: MentionItem[];
}) {
  if (!value || typeof value !== 'object') return null;
  const activate = (target: EventTarget) => {
    const id = (target as HTMLElement).closest<HTMLElement>('[data-task-id]')?.dataset.taskId;
    if (id) onTaskClick?.(id);
  };
  return (
    <div
      className={cn('rich-text text-sm', className)}
      onClick={(e) => activate(e.target)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') activate(e.target);
      }}
    >
      {renderNode(value as RichNode, 0, people)}
    </div>
  );
}
